import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRevenueCat } from '@/hooks/useRevenueCat';
import { SubscriptionTier, hasFeature, getSubscriptionLimit, FEATURE_FLAGS } from '@/lib/entitlements';
import { UpgradeModal } from '@/components/paywall/UpgradeModal';

interface SubscriptionContextType {
    tier: SubscriptionTier;
    isLoading: boolean;
    canAccessFeature: (feature: keyof typeof FEATURE_FLAGS) => boolean;
    subscriptionLimit: number;
    showUpgradeModal: (feature: string, requiredTier: SubscriptionTier) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const { checkEntitlement, isLoading: rcLoading } = useRevenueCat();
    const [tier, setTier] = useState<SubscriptionTier>('free');
    const [isLoading, setIsLoading] = useState(true);
    const [upgradeModal, setUpgradeModal] = useState<{ open: boolean; feature: string; requiredTier: SubscriptionTier }>({
        open: false,
        feature: '',
        requiredTier: 'plus',
    });

    useEffect(() => {
        async function determineTier() {
            if (rcLoading) return;

            try {
                // Check for highest tier first
                const hasPro = await checkEntitlement('pro');
                if (hasPro) {
                    setTier('pro');
                    setIsLoading(false);
                    return;
                }

                const hasPlus = await checkEntitlement('plus');
                if (hasPlus) {
                    setTier('plus');
                    setIsLoading(false);
                    return;
                }

                setTier('free');
            } catch (error) {
                console.error('Failed to determine subscription tier:', error);
                setTier('free'); // Fallback to free
            } finally {
                setIsLoading(false);
            }
        }

        determineTier();
    }, [checkEntitlement, rcLoading]);

    const canAccessFeature = (feature: keyof typeof FEATURE_FLAGS) => hasFeature(tier, feature);
    const subscriptionLimit = getSubscriptionLimit(tier);

    const showUpgradeModal = (feature: string, requiredTier: SubscriptionTier) => {
        setUpgradeModal({ open: true, feature, requiredTier });
    };

    return (
        <SubscriptionContext.Provider value={{ tier, isLoading, canAccessFeature, subscriptionLimit, showUpgradeModal }}>
            {children}
            <UpgradeModal
                isOpen={upgradeModal.open}
                onClose={() => setUpgradeModal(prev => ({ ...prev, open: false }))}
                feature={upgradeModal.feature}
                requiredTier={upgradeModal.requiredTier}
            />
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (!context) throw new Error('useSubscription must be used within SubscriptionProvider');
    return context;
}
